"use client";

import React, { useState } from "react";
import { FooterWithChildren } from "@repo/ui/v2";
import BrandedActionButton from "src/components/branded-action-button";
import type { BillingPeriod } from "../hooks/use-paywall-state";
import type { ProductType } from "../hooks/use-paywall-state";
import { supportsExpressCheckout } from "src/lib/express-checkout-utils";
import { PaywallExpressCheckout } from "./paywall-express-checkout";

interface PaywallFooterProps {
  productTitle: string; // "Fast RX" or "Pediatric Care"
  billingPeriod: BillingPeriod;
  selectedProduct: ProductType;
  onStartTrial: () => void;
  hasFreeTrial: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Whether to show express checkout (Apple Pay / Google Pay) — controlled by Statsig dynamic config */
  showExpressCheckout?: boolean;
  /** Optional dismiss handler — renders a "NOT RIGHT NOW" button when provided */
  onDismiss?: () => void;
}

/**
 * Sticky footer component containing Apple Pay / Google Pay and the CTA button
 * Uses FooterWithChildren with custom styling to match Figma design
 */
export function PaywallFooter({
  productTitle,
  billingPeriod,
  selectedProduct,
  onStartTrial,
  hasFreeTrial,
  disabled,
  loading,
  showExpressCheckout = false,
  onDismiss,
}: PaywallFooterProps) {
  const [isExpressProcessing, setIsExpressProcessing] = useState(false);
  const [expressAvailable, setExpressAvailable] = useState(false);

  // Set button text based on whether there's a free trial
  const buttonText = hasFreeTrial ? "Start Free Trial" : "Get Now";
  const isDisabled = disabled || isExpressProcessing;

  const showExpress =
    showExpressCheckout && supportsExpressCheckout(selectedProduct);

  return (
    <FooterWithChildren
      position="fixed"
      className="fixed bottom-0 left-0 right-0 z-10 backdrop-blur-[12.5px] bg-[rgba(0,0,0,0.35)] border-t border-[rgba(255,255,255,0.09)] pb-[calc(16px+var(--felt-safe-area-bottom,0px))] pt-[24px] px-[24px]"
      theme={{
        base: "",
        positions: {
          fixed: "fixed bottom-0 left-0 right-0",
        },
        container: "flex flex-col gap-[16px] items-start w-full",
      }}
    >
      {/* Express checkout — only available for Fast Prescriptions (not Kids Care yet) */}
      {showExpress && (
        <div className="w-full">
          <PaywallExpressCheckout
            selectedProduct={selectedProduct}
            billingPeriod={billingPeriod}
            setIsProcessing={setIsExpressProcessing}
            onAvailabilityChange={setExpressAvailable}
          />
          {/* OR Divider — reserve the height up-front so the footer doesn't
              shift when Stripe resolves availability. Content fills the
              reserved space once Apple Pay / Google Pay is confirmed (PE-1291). */}
          <div className="mt-[16px] h-[21px] flex items-center">
            {expressAvailable && (
              <>
                <div className="flex-1 border-t border-white/20"></div>
                <span className="px-4 text-sm text-slate-300">OR</span>
                <div className="flex-1 border-t border-white/20"></div>
              </>
            )}
          </div>
        </div>
      )}
      <BrandedActionButton
        text={buttonText}
        onPress={onStartTrial}
        eventData={{
          product: productTitle, // "Fast RX" or "Pediatric Care"
          billingPeriod,
          "Free trial": hasFreeTrial.toString(),
        }}
        className="!bg-teal-700 hover:!bg-teal-700 h-[44px] w-full rounded-[99px]"
        variant="primary"
        disabled={isDisabled}
        loading={loading}
      />
      {onDismiss && !showExpress && (
        <BrandedActionButton
          text="NOT RIGHT NOW"
          onPress={onDismiss}
          eventData={{
            action: "dismiss_paywall",
            source: "paywall_footer",
          }}
          variant="tertiary"
          className="w-full !text-slate-300"
        />
      )}
    </FooterWithChildren>
  );
}
